import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore'
import SearchOffIcon from '@mui/icons-material/SearchOff'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ListAltIcon from '@mui/icons-material/ListAlt'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'

const ParticipantTableSection = ({
  // Filter states
  nameFilter,
  setNameFilter,
  statusFilter,
  setStatusFilter,
  bibFilter,
  rowFilter,
  genderFilter,
  activeShirtTypeFilter,
  activeShirtSizeFilter,
  // Handlers
  onFilterModalOpen,
  onExport,
  onRemoveFilter,
  onClear,
  onSort,
  sortField,
  sortDirection,
  // Selection
  selectedItems,
  selectAll,
  totalParticipants,
  sortedParticipants,
  onCheckboxChange,
  onSelectAllChange,
  onDeleteClick,
  onClearSelection,
  // Table data
  loading,
  participants,
  paginatedParticipants,
  hasActiveFilters,
  // Pagination
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalPages,
  // Details dialog
  onDetailsClick,
  onDeleteParticipantClick,
  onProfileClick,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box sx={{ mb: { xs: 2.5, sm: 3, md: 3.5 } }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 1,
          overflow: 'hidden',
          boxShadow: (theme) =>
            theme.palette.mode === 'light'
              ? '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)'
              : '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.4)',
        }}>
        {/* Filter and Search Controls - Inside Table Container */}
        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            borderBottom: (theme) =>
              `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(249, 250, 251, 0.5)' : 'rgba(18, 18, 18, 0.5)',
          }}>
          <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
            {/* Filter Inputs Row */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
              alignItems={{ xs: 'stretch', sm: 'center' }}>
              {/* Left Side - Filter Inputs */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1.25, sm: 1.5, md: 2 }}
                sx={{ flex: 1, minWidth: 0 }}>
                {/* Name Input */}
                <TextField
                  label="Name"
                  placeholder="e.g. Somchai Jaidee"
                  value={nameFilter}
                  onChange={(e) => setNameFilter(e.target.value)}
                  size="small"
                  sx={{
                    flex: { xs: '1 1 100%', sm: '1 1 auto', md: '1 1 300px' },
                    minWidth: { xs: '100%', sm: 200, md: 250 },
                    '& .MuiOutlinedInput-root': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      borderRadius: 1,
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(255, 255, 255, 1)'
                          : 'rgba(18, 18, 18, 1)',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                    },
                  }}
                />

                {/* Status Dropdown */}
                <FormControl
                  size="small"
                  sx={{
                    flex: { xs: '1 1 100%', sm: '0 0 auto', md: '0 0 150px' },
                    minWidth: { xs: '100%', sm: 120, md: 150 },
                  }}>
                  <InputLabel
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                    }}>
                    Status
                  </InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                    sx={{
                      fontSize: {
                        xs: '0.8125rem',
                        sm: '0.875rem',
                        md: '0.9375rem',
                      },
                      borderRadius: 1,
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(255, 255, 255, 1)'
                          : 'rgba(18, 18, 18, 1)',
                      '&:hover': {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'primary.main',
                        },
                      },
                    }}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="checkin">checkin</MenuItem>
                    <MenuItem value="pickup">pickup</MenuItem>
                  </Select>
                </FormControl>
              </Stack>

              {/* Right Side - Action Buttons */}
              <Stack
                direction={{ xs: 'row', sm: 'row' }}
                spacing={{ xs: 1, sm: 1.25, md: 1.5 }}
                sx={{ flexShrink: 0 }}>
                <Button
                  type="button"
                  onClick={onFilterModalOpen}
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    fontWeight: 500,
                    textTransform: 'none',
                    py: { xs: 1, sm: 1.125, md: 1.25 },
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.23)'
                        : 'rgba(255, 255, 255, 0.23)',
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.87)'
                        : 'rgba(255, 255, 255, 0.87)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(25, 118, 210, 0.04)'
                          : 'rgba(144, 202, 249, 0.08)',
                    },
                  }}>
                  Filter
                </Button>
                <Button
                  onClick={onExport}
                  variant="contained"
                  color="success"
                  startIcon={<FileDownloadIcon />}
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    fontWeight: 600,
                    textTransform: 'none',
                    py: { xs: 1, sm: 1.125, md: 1.25 },
                    px: { xs: 1.5, sm: 2, md: 2.5 },
                    boxShadow: (theme) =>
                      theme.palette.mode === 'light'
                        ? '0 2px 4px rgba(0, 0, 0, 0.1)'
                        : '0 2px 4px rgba(0, 0, 0, 0.3)',
                    '&:hover': {
                      boxShadow: (theme) =>
                        theme.palette.mode === 'light'
                          ? '0 4px 8px rgba(0, 0, 0, 0.15)'
                          : '0 4px 8px rgba(0, 0, 0, 0.4)',
                    },
                  }}>
                  Export
                </Button>
              </Stack>
            </Stack>

            {/* Active Filters Badges - Only show if there are filters other than name */}
            {((bibFilter !== '' && bibFilter !== 'All') ||
              (rowFilter !== '' && rowFilter !== 'All') ||
              (genderFilter !== '' && genderFilter !== 'All') ||
              (statusFilter !== '' && statusFilter !== 'All') ||
              activeShirtTypeFilter !== null ||
              activeShirtSizeFilter !== null) && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 0.75, sm: 1, md: 1.25 },
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pt: { xs: 1, sm: 1.25, md: 1.5 },
                  borderTop: (theme) =>
                    `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)'}`,
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 0.75, sm: 1, md: 1.25 },
                    alignItems: 'center',
                    flex: 1,
                  }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.8125rem',
                        md: '0.875rem',
                      },
                      fontWeight: 500,
                      mr: { xs: 0.5, sm: 0.75, md: 1 },
                    }}>
                    Active Filters:
                  </Typography>
                  {bibFilter !== '' && bibFilter !== 'All' && (
                    <Chip
                      label={`Bib: ${bibFilter}`}
                      onDelete={() => onRemoveFilter('bib')}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: {
                              xs: '0.875rem',
                              sm: '1rem',
                              md: '1.125rem',
                            },
                          }}
                        />
                      }
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(144, 202, 249, 0.2)',
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.9)'
                            : 'rgba(144, 202, 249, 0.9)',
                        fontSize: {
                          xs: '0.6875rem',
                          sm: '0.75rem',
                          md: '0.8125rem',
                        },
                        height: { xs: 20, sm: 22, md: 24 },
                        '& .MuiChip-deleteIcon': {
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(25, 118, 210, 0.7)'
                              : 'rgba(144, 202, 249, 0.7)',
                          '&:hover': {
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 1)'
                                : 'rgba(144, 202, 249, 1)',
                          },
                        },
                      }}
                    />
                  )}
                  {rowFilter !== '' && rowFilter !== 'All' && (
                    <Chip
                      label={`Row: ${rowFilter}`}
                      onDelete={() => onRemoveFilter('row')}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: {
                              xs: '0.875rem',
                              sm: '1rem',
                              md: '1.125rem',
                            },
                          }}
                        />
                      }
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(144, 202, 249, 0.2)',
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.9)'
                            : 'rgba(144, 202, 249, 0.9)',
                        fontSize: {
                          xs: '0.6875rem',
                          sm: '0.75rem',
                          md: '0.8125rem',
                        },
                        height: { xs: 20, sm: 22, md: 24 },
                        '& .MuiChip-deleteIcon': {
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(25, 118, 210, 0.7)'
                              : 'rgba(144, 202, 249, 0.7)',
                          '&:hover': {
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 1)'
                                : 'rgba(144, 202, 249, 1)',
                          },
                        },
                      }}
                    />
                  )}
                  {genderFilter !== '' && genderFilter !== 'All' && (
                    <Chip
                      label={`Gender: ${genderFilter}`}
                      onDelete={() => onRemoveFilter('gender')}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: {
                              xs: '0.875rem',
                              sm: '1rem',
                              md: '1.125rem',
                            },
                          }}
                        />
                      }
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(144, 202, 249, 0.2)',
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.9)'
                            : 'rgba(144, 202, 249, 0.9)',
                        fontSize: {
                          xs: '0.6875rem',
                          sm: '0.75rem',
                          md: '0.8125rem',
                        },
                        height: { xs: 20, sm: 22, md: 24 },
                        '& .MuiChip-deleteIcon': {
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(25, 118, 210, 0.7)'
                              : 'rgba(144, 202, 249, 0.7)',
                          '&:hover': {
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 1)'
                                : 'rgba(144, 202, 249, 1)',
                          },
                        },
                      }}
                    />
                  )}
                  {statusFilter !== '' && statusFilter !== 'All' && (
                    <Chip
                      label={`Status: ${statusFilter}`}
                      onDelete={() => onRemoveFilter('status')}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: {
                              xs: '0.875rem',
                              sm: '1rem',
                              md: '1.125rem',
                            },
                          }}
                        />
                      }
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(144, 202, 249, 0.2)',
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.9)'
                            : 'rgba(144, 202, 249, 0.9)',
                        fontSize: {
                          xs: '0.6875rem',
                          sm: '0.75rem',
                          md: '0.8125rem',
                        },
                        height: { xs: 20, sm: 22, md: 24 },
                        '& .MuiChip-deleteIcon': {
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(25, 118, 210, 0.7)'
                              : 'rgba(144, 202, 249, 0.7)',
                          '&:hover': {
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 1)'
                                : 'rgba(144, 202, 249, 1)',
                          },
                        },
                      }}
                    />
                  )}
                  {activeShirtTypeFilter && (
                    <Chip
                      label={`Shirt Type: ${activeShirtTypeFilter}`}
                      onDelete={() => onRemoveFilter('shirtType')}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: {
                              xs: '0.875rem',
                              sm: '1rem',
                              md: '1.125rem',
                            },
                          }}
                        />
                      }
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(144, 202, 249, 0.2)',
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.9)'
                            : 'rgba(144, 202, 249, 0.9)',
                        fontSize: {
                          xs: '0.6875rem',
                          sm: '0.75rem',
                          md: '0.8125rem',
                        },
                        height: { xs: 20, sm: 22, md: 24 },
                        '& .MuiChip-deleteIcon': {
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(25, 118, 210, 0.7)'
                              : 'rgba(144, 202, 249, 0.7)',
                          '&:hover': {
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 1)'
                                : 'rgba(144, 202, 249, 1)',
                          },
                        },
                      }}
                    />
                  )}
                  {activeShirtSizeFilter && (
                    <Chip
                      label={`Shirt Size: ${activeShirtSizeFilter}`}
                      onDelete={() => onRemoveFilter('shirtSize')}
                      deleteIcon={
                        <CloseIcon
                          sx={{
                            fontSize: {
                              xs: '0.875rem',
                              sm: '1rem',
                              md: '1.125rem',
                            },
                          }}
                        />
                      }
                      size="small"
                      sx={{
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.1)'
                            : 'rgba(144, 202, 249, 0.2)',
                        color: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(25, 118, 210, 0.9)'
                            : 'rgba(144, 202, 249, 0.9)',
                        fontSize: {
                          xs: '0.6875rem',
                          sm: '0.75rem',
                          md: '0.8125rem',
                        },
                        height: { xs: 20, sm: 22, md: 24 },
                        '& .MuiChip-deleteIcon': {
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(25, 118, 210, 0.7)'
                              : 'rgba(144, 202, 249, 0.7)',
                          '&:hover': {
                            color: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 1)'
                                : 'rgba(144, 202, 249, 1)',
                          },
                        },
                      }}
                    />
                  )}
                </Box>
                {/* Clear Button - Inline with Active Filters */}
                <Button
                  onClick={onClear}
                  variant="outlined"
                  color="primary"
                  size="small"
                  sx={{
                    borderRadius: 1,
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8125rem',
                      md: '0.875rem',
                    },
                    fontWeight: 500,
                    textTransform: 'none',
                    py: { xs: 0.5, sm: 0.625, md: 0.75 },
                    px: { xs: 1, sm: 1.25, md: 1.5 },
                    minWidth: 'auto',
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.23)'
                        : 'rgba(255, 255, 255, 0.23)',
                    color: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.87)'
                        : 'rgba(255, 255, 255, 0.87)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? 'rgba(25, 118, 210, 0.04)'
                          : 'rgba(144, 202, 249, 0.08)',
                    },
                  }}>
                  Clear
                </Button>
              </Box>
            )}

            {/* Selected Items Indicator - Simplified */}
            {(selectedItems.length > 0 || selectAll) && (
              <Box
                sx={{
                  mt: { xs: 1.25, sm: 1.5, md: 2 },
                  py: { xs: 0.75, sm: 1, md: 1.25 },
                  px: { xs: 1, sm: 1.25, md: 1.5 },
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(25, 118, 210, 0.08)'
                      : 'rgba(144, 202, 249, 0.16)',
                  border: (theme) =>
                    `1px solid ${theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.2)' : 'rgba(144, 202, 249, 0.3)'}`,
                }}>
                <Stack
                  direction="row"
                  spacing={{ xs: 1, sm: 1.25, md: 1.5 }}
                  alignItems="center"
                  justifyContent="space-between">
                  <Typography
                    sx={{
                      fontSize: {
                        xs: '0.75rem',
                        sm: '0.8125rem',
                        md: '0.875rem',
                      },
                      fontWeight: 500,
                      color: 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 0.5, sm: 0.75, md: 1 },
                    }}>
                    <Checkbox
                      checked
                      size="small"
                      sx={{
                        color: 'primary.main',
                        p: 0,
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: {
                            xs: '1rem',
                            sm: '1.125rem',
                            md: '1.25rem',
                          },
                        },
                      }}
                    />
                    {selectAll
                      ? `All ${totalParticipants} participants selected`
                      : `${selectedItems.length} participant${selectedItems.length !== 1 ? 's' : ''} selected`}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={{ xs: 0.75, sm: 1, md: 1.25 }}
                    alignItems="center">
                    <Button
                      onClick={onDeleteClick}
                      variant="contained"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      size="small"
                      sx={{
                        fontSize: {
                          xs: '0.75rem',
                          sm: '0.8125rem',
                          md: '0.875rem',
                        },
                        fontWeight: 500,
                        textTransform: 'none',
                        px: { xs: 1, sm: 1.25, md: 1.5 },
                        py: { xs: 0.5, sm: 0.625, md: 0.75 },
                        minWidth: 'auto',
                        boxShadow: (theme) =>
                          theme.palette.mode === 'light'
                            ? '0 2px 4px rgba(244, 67, 54, 0.2)'
                            : '0 2px 4px rgba(244, 67, 54, 0.4)',
                        '&:hover': {
                          boxShadow: (theme) =>
                            theme.palette.mode === 'light'
                              ? '0 4px 8px rgba(244, 67, 54, 0.3)'
                              : '0 4px 8px rgba(244, 67, 54, 0.5)',
                        },
                      }}>
                      Delete
                    </Button>
                    <Button
                      onClick={onClearSelection}
                      size="small"
                      sx={{
                        fontSize: {
                          xs: '0.75rem',
                          sm: '0.8125rem',
                          md: '0.875rem',
                        },
                        textTransform: 'none',
                        color: 'text.secondary',
                        minWidth: 'auto',
                        px: { xs: 1, sm: 1.25, md: 1.5 },
                        py: { xs: 0.5, sm: 0.625, md: 0.75 },
                        '&:hover': {
                          color: 'error.main',
                          backgroundColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(244, 67, 54, 0.08)'
                              : 'rgba(244, 67, 54, 0.16)',
                        },
                      }}>
                      Clear
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}>
          <Table sx={{ minWidth: 650 }} aria-label="participants table">
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: (theme) =>
                  theme.palette.mode === 'light'
                    ? 'rgba(0, 0, 0, 0.04)'
                    : 'rgba(255, 255, 255, 0.08)',
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.06)'
                      : 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <TableCell
                padding="checkbox"
                sx={{
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 1.5, sm: 2, md: 2.5 },
                  width: { xs: 48, sm: 56, md: 64 },
                }}>
                <Checkbox
                  color="primary"
                  checked={
                    selectAll ||
                    (paginatedParticipants.length > 0 &&
                      paginatedParticipants.every((row) =>
                        selectedItems.some((selected) => selected._id === row._id)
                      ))
                  }
                  indeterminate={
                    !selectAll &&
                    paginatedParticipants.some((row) =>
                      selectedItems.some((selected) => selected._id === row._id)
                    ) &&
                    !paginatedParticipants.every((row) =>
                      selectedItems.some((selected) => selected._id === row._id)
                    )
                  }
                  onChange={onSelectAllChange}
                  size="small"
                  sx={{
                    '&.Mui-checked': {
                      color: 'primary.main',
                    },
                    '&.MuiCheckbox-indeterminate': {
                      color: 'primary.main',
                    },
                  }}
                />
              </TableCell>
              <TableCell
                onClick={() => onSort('name')}
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.04)',
                  },
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Full Name
                  </Typography>
                  {sortField === 'name' ? (
                    sortDirection === 'asc' ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    )
                  ) : (
                    <UnfoldMoreIcon
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                          md: '1.125rem',
                        },
                        color: 'text.disabled',
                        opacity: 0.5,
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell
                onClick={() => onSort('gender')}
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.04)',
                  },
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Gender
                  </Typography>
                  {sortField === 'gender' ? (
                    sortDirection === 'asc' ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    )
                  ) : (
                    <UnfoldMoreIcon
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                          md: '1.125rem',
                        },
                        color: 'text.disabled',
                        opacity: 0.5,
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell
                align="right"
                onClick={() => onSort('bib')}
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.04)',
                  },
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Bib
                  </Typography>
                  {sortField === 'bib' ? (
                    sortDirection === 'asc' ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    )
                  ) : (
                    <UnfoldMoreIcon
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                          md: '1.125rem',
                        },
                        color: 'text.disabled',
                        opacity: 0.5,
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell
                onClick={() => onSort('row')}
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.04)',
                  },
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Row
                  </Typography>
                  {sortField === 'row' ? (
                    sortDirection === 'asc' ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    )
                  ) : (
                    <UnfoldMoreIcon
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                          md: '1.125rem',
                        },
                        color: 'text.disabled',
                        opacity: 0.5,
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell
                onClick={() => onSort('status')}
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.04)',
                  },
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Status
                  </Typography>
                  {sortField === 'status' ? (
                    sortDirection === 'asc' ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    )
                  ) : (
                    <UnfoldMoreIcon
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                          md: '1.125rem',
                        },
                        color: 'text.disabled',
                        opacity: 0.5,
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell
                onClick={() => onSort('shirtType')}
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.04)',
                  },
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Shirt Type
                  </Typography>
                  {sortField === 'shirtType' ? (
                    sortDirection === 'asc' ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    )
                  ) : (
                    <UnfoldMoreIcon
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                          md: '1.125rem',
                        },
                        color: 'text.disabled',
                        opacity: 0.5,
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell
                onClick={() => onSort('shirtSize')}
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 2, sm: 2.5, md: 3 },
                  cursor: 'pointer',
                  userSelect: 'none',
                  '&:hover': {
                    backgroundColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.02)'
                        : 'rgba(255, 255, 255, 0.04)',
                  },
                }}>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Shirt Size
                  </Typography>
                  {sortField === 'shirtSize' ? (
                    sortDirection === 'asc' ? (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    ) : (
                      <ArrowDownwardIcon
                        sx={{
                          fontSize: {
                            xs: '0.875rem',
                            sm: '1rem',
                            md: '1.125rem',
                          },
                        }}
                      />
                    )
                  ) : (
                    <UnfoldMoreIcon
                      sx={{
                        fontSize: {
                          xs: '0.875rem',
                          sm: '1rem',
                          md: '1.125rem',
                        },
                        color: 'text.disabled',
                        opacity: 0.5,
                      }}
                    />
                  )}
                </Stack>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  color: 'text.primary',
                  py: { xs: 1.25, sm: 1.5, md: 1.75 },
                  px: { xs: 1.5, sm: 2, md: 2.5 },
                  width: { xs: 120, sm: 140, md: 160 },
                }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{
                    py: { xs: 4, sm: 5, md: 6 },
                    color: 'text.secondary',
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                  }}>
                  Loading...
                </TableCell>
              </TableRow>
            ) : !participants || paginatedParticipants.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{
                    py: { xs: 6, sm: 8, md: 10 },
                    px: { xs: 2, sm: 3, md: 4 },
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: { xs: 1.5, sm: 2, md: 2.5 },
                    }}>
                    <Box
                      sx={{
                        width: { xs: 80, sm: 100, md: 120 },
                        height: { xs: 80, sm: 100, md: 120 },
                        borderRadius: '50%',
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.04)'
                            : 'rgba(255, 255, 255, 0.08)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: { xs: 1, sm: 1.5, md: 2 },
                      }}>
                      <SearchOffIcon
                        sx={{
                          fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                          color: 'text.secondary',
                          opacity: 0.6,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                        mb: { xs: 0.5, sm: 0.75, md: 1 },
                      }}>
                      No participants found
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        textAlign: 'center',
                        maxWidth: { xs: '100%', sm: 400, md: 500 },
                        lineHeight: 1.6,
                      }}>
                      {hasActiveFilters
                        ? "Try adjusting your search or filter criteria to find what you're looking for."
                        : 'No participants have been added to this event yet.'}
                    </Typography>
                    {hasActiveFilters && (
                      <Button
                        onClick={onClear}
                        variant="outlined"
                        color="primary"
                        size="small"
                        sx={{
                          mt: { xs: 1, sm: 1.5, md: 2 },
                          borderRadius: 1,
                          fontSize: {
                            xs: '0.8125rem',
                            sm: '0.875rem',
                            md: '0.9375rem',
                          },
                          fontWeight: 500,
                          textTransform: 'none',
                          py: { xs: 1, sm: 1.125, md: 1.25 },
                          px: { xs: 2, sm: 2.5, md: 3 },
                          borderColor: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.23)'
                              : 'rgba(255, 255, 255, 0.23)',
                          color: (theme) =>
                            theme.palette.mode === 'light'
                              ? 'rgba(0, 0, 0, 0.87)'
                              : 'rgba(255, 255, 255, 0.87)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            backgroundColor: (theme) =>
                              theme.palette.mode === 'light'
                                ? 'rgba(25, 118, 210, 0.04)'
                                : 'rgba(144, 202, 249, 0.08)',
                          },
                        }}>
                        Clear Filters
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedParticipants.map((row, index) => {
                const isSelected = selectedItems.some((selected) => selected._id === row._id)
                const status = row.lastActivity?.status || '-'
                const hasWarning = false // You can add logic to determine if participant has warning

                return (
                  <TableRow
                    key={row._id}
                    sx={{
                      backgroundColor: (theme) =>
                        index % 2 === 0
                          ? theme.palette.mode === 'light'
                            ? 'rgba(255, 255, 255, 1)'
                            : 'rgba(18, 18, 18, 1)'
                          : theme.palette.mode === 'light'
                            ? 'rgba(249, 250, 251, 1)'
                            : 'rgba(30, 30, 30, 1)',
                      '&:hover': {
                        backgroundColor: (theme) =>
                          theme.palette.mode === 'light'
                            ? 'rgba(245, 245, 245, 1)'
                            : 'rgba(40, 40, 40, 1)',
                      },
                      transition: (theme) =>
                        theme.transitions.create(['background-color'], {
                          duration: theme.transitions.duration.short,
                        }),
                    }}>
                    <TableCell
                      padding="checkbox"
                      sx={{
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 1.5, sm: 2, md: 2.5 },
                      }}>
                      <Checkbox
                        color="primary"
                        checked={isSelected}
                        onChange={() => onCheckboxChange(row)}
                        size="small"
                        sx={{
                          '&.Mui-checked': {
                            color: 'primary.main',
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 2, sm: 2.5, md: 3 },
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}>
                      {row.firstname || '-'} {row.lastname || ''}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 2, sm: 2.5, md: 3 },
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}>
                      {row.gender || '-'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 2, sm: 2.5, md: 3 },
                        fontFamily: 'monospace',
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}>
                      {row.bib || '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 2, sm: 2.5, md: 3 },
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}>
                      {row.row || '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 2, sm: 2.5, md: 3 },
                      }}>
                      {status !== '-' ? (
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            backgroundColor:
                              status === 'checkin'
                                ? (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'rgba(76, 175, 80, 0.1)'
                                      : 'rgba(76, 175, 80, 0.2)'
                                : (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'rgba(33, 150, 243, 0.1)'
                                      : 'rgba(33, 150, 243, 0.2)',
                            color:
                              status === 'checkin'
                                ? (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'rgba(76, 175, 80, 0.9)'
                                      : 'rgba(129, 199, 132, 0.9)'
                                : (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'rgba(33, 150, 243, 0.9)'
                                      : 'rgba(144, 202, 249, 0.9)',
                            fontWeight: 500,
                            fontSize: {
                              xs: '0.6875rem',
                              sm: '0.75rem',
                              md: '0.8125rem',
                            },
                            height: { xs: 20, sm: 22, md: 24 },
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        <Typography
                          sx={{
                            fontSize: {
                              xs: '0.8125rem',
                              sm: '0.875rem',
                              md: '0.9375rem',
                            },
                            color: 'text.secondary',
                          }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 2, sm: 2.5, md: 3 },
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}>
                      {row.shirt?.type || '-'}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: {
                          xs: '0.8125rem',
                          sm: '0.875rem',
                          md: '0.9375rem',
                        },
                        color: 'text.primary',
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 2, sm: 2.5, md: 3 },
                        fontWeight: 400,
                        lineHeight: 1.5,
                      }}>
                      {row.shirt?.size || '-'}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        py: { xs: 1.25, sm: 1.5, md: 1.75 },
                        px: { xs: 1.5, sm: 2, md: 2.5 },
                      }}>
                      <Stack
                        direction="row"
                        spacing={{ xs: 0.5, sm: 0.75, md: 1 }}
                        justifyContent="center"
                        alignItems="center">
                        {hasWarning && (
                          <Tooltip title="Warning" arrow placement="top">
                            <IconButton
                              size="small"
                              sx={{
                                color: 'warning.main',
                                width: { xs: 32, sm: 36, md: 40 },
                                height: { xs: 32, sm: 36, md: 40 },
                                '&:hover': {
                                  backgroundColor: (theme) =>
                                    theme.palette.mode === 'light'
                                      ? 'rgba(255, 152, 0, 0.08)'
                                      : 'rgba(255, 152, 0, 0.16)',
                                },
                              }}>
                              <ReportProblemOutlinedIcon
                                sx={{
                                  fontSize: {
                                    xs: '1rem',
                                    sm: '1.125rem',
                                    md: '1.25rem',
                                  },
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Profile" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => onProfileClick && onProfileClick(row)}
                            sx={{
                              color: 'text.secondary',
                              width: { xs: 32, sm: 36, md: 40 },
                              height: { xs: 32, sm: 36, md: 40 },
                              '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'rgba(25, 118, 210, 0.08)'
                                    : 'rgba(144, 202, 249, 0.16)',
                                transform: 'scale(1.1)',
                              },
                              transition: (theme) =>
                                theme.transitions.create(
                                  ['color', 'background-color', 'transform'],
                                  {
                                    duration: theme.transitions.duration.short,
                                  }
                                ),
                            }}>
                            <PersonOutlineIcon
                              sx={{
                                fontSize: {
                                  xs: '1rem',
                                  sm: '1.125rem',
                                  md: '1.25rem',
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Details" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => onDetailsClick(row)}
                            sx={{
                              color: 'text.secondary',
                              width: { xs: 32, sm: 36, md: 40 },
                              height: { xs: 32, sm: 36, md: 40 },
                              '&:hover': {
                                color: 'primary.main',
                                backgroundColor: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'rgba(25, 118, 210, 0.08)'
                                    : 'rgba(144, 202, 249, 0.16)',
                                transform: 'scale(1.1)',
                              },
                              transition: (theme) =>
                                theme.transitions.create(
                                  ['color', 'background-color', 'transform'],
                                  {
                                    duration: theme.transitions.duration.short,
                                  }
                                ),
                            }}>
                            <ListAltIcon
                              sx={{
                                fontSize: {
                                  xs: '1rem',
                                  sm: '1.125rem',
                                  md: '1.25rem',
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={() => onDeleteParticipantClick(row)}
                            sx={{
                              color: 'text.secondary',
                              width: { xs: 32, sm: 36, md: 40 },
                              height: { xs: 32, sm: 36, md: 40 },
                              '&:hover': {
                                color: 'error.main',
                                backgroundColor: (theme) =>
                                  theme.palette.mode === 'light'
                                    ? 'rgba(244, 67, 54, 0.08)'
                                    : 'rgba(244, 67, 54, 0.16)',
                                transform: 'scale(1.1)',
                              },
                              transition: (theme) =>
                                theme.transitions.create(
                                  ['color', 'background-color', 'transform'],
                                  {
                                    duration: theme.transitions.duration.short,
                                  }
                                ),
                            }}>
                            <DeleteOutlineIcon
                              sx={{
                                fontSize: {
                                  xs: '1rem',
                                  sm: '1.125rem',
                                  md: '1.25rem',
                                },
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
        </Box>
      </TableContainer>

      {/* Pagination: mobile/tablet = stacked layout; desktop = same row */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', md: 'row' },
          flexWrap: 'nowrap',
          justifyContent: { xs: 'flex-start', sm: 'flex-start', md: 'space-between' },
          alignItems: { xs: 'stretch', sm: 'stretch', md: 'center' },
          gap: { xs: 2, sm: 2, md: 2.5 },
          py: { xs: 2, sm: 2, md: 2 },
          px: { xs: 2, sm: 2.5, md: 3 },
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light' ? 'rgba(249, 250, 251, 0.5)' : 'rgba(18, 18, 18, 0.5)',
        }}>
        {/* Mobile/Tablet: Row 1 = Showing + Rows per page | Row 2 = Pagination (centered) */}
        {/* Desktop: Left block = Showing + Rows per page, Right = Pagination */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row', md: 'row' },
            alignItems: { xs: 'stretch', sm: 'center', md: 'center' },
            gap: { xs: 1.5, sm: 2.5, md: 3 },
            flex: { xs: '0 0 auto', sm: '0 0 auto', md: 1 },
            minWidth: 0,
          }}>
          {/* Showing Info */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              fontWeight: 400,
              lineHeight: 1.5,
              display: 'flex',
              alignItems: 'center',
              whiteSpace: { xs: 'normal', sm: 'nowrap', md: 'nowrap' },
            }}>
            {totalParticipants > 0 ? (
              <>
                Showing{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mx: 0.25,
                  }}>
                  {(page - 1) * rowsPerPage + 1}
                </Box>
                {' - '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mx: 0.25,
                  }}>
                  {Math.min(page * rowsPerPage, totalParticipants)}
                </Box>
                {' of '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    mx: 0.25,
                  }}>
                  {totalParticipants}
                </Box>
                {' participants'}
              </>
            ) : (
              'No participants found'
            )}
          </Typography>

          {/* Rows Per Page Selector */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 1, sm: 1.25, md: 1.5 },
              flexShrink: 0,
            }}>
            <Typography
              sx={{
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                color: 'text.secondary',
                fontWeight: 400,
                whiteSpace: 'nowrap',
              }}>
              Rows per page:
            </Typography>
            <FormControl
              size="small"
              sx={{
                minWidth: { xs: 72, sm: 80, md: 90 },
              }}>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(e.target.value)
                  setPage(1)
                }}
                sx={{
                  fontSize: {
                    xs: '0.8125rem',
                    sm: '0.875rem',
                    md: '0.9375rem',
                  },
                  height: { xs: 36, sm: 36, md: 40 },
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(255, 255, 255, 1)'
                      : 'rgba(18, 18, 18, 1)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: (theme) =>
                      theme.palette.mode === 'light'
                        ? 'rgba(0, 0, 0, 0.23)'
                        : 'rgba(255, 255, 255, 0.23)',
                  },
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                    },
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: 1,
                      mt: 0.5,
                      boxShadow: (theme) =>
                        theme.palette.mode === 'light'
                          ? '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)'
                          : '0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)',
                    },
                  },
                }}>
                <MenuItem
                  value={5}
                  sx={{
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    py: { xs: 0.75, sm: 0.875, md: 1 },
                  }}>
                  5
                </MenuItem>
                <MenuItem
                  value={10}
                  sx={{
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    py: { xs: 0.75, sm: 0.875, md: 1 },
                  }}>
                  10
                </MenuItem>
                <MenuItem
                  value={25}
                  sx={{
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    py: { xs: 0.75, sm: 0.875, md: 1 },
                  }}>
                  25
                </MenuItem>
                <MenuItem
                  value={50}
                  sx={{
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    py: { xs: 0.75, sm: 0.875, md: 1 },
                  }}>
                  50
                </MenuItem>
                <MenuItem
                  value={100}
                  sx={{
                    fontSize: {
                      xs: '0.8125rem',
                      sm: '0.875rem',
                      md: '0.9375rem',
                    },
                    py: { xs: 0.75, sm: 0.875, md: 1 },
                  }}>
                  100
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Pagination Controls: centered on mobile/tablet, right on desktop */}
        {totalPages > 1 && (
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              justifyContent: { xs: 'center', sm: 'center', md: 'flex-end' },
              overflowX: 'auto',
              overflowY: 'hidden',
              py: { xs: 0.5, md: 0 },
              mx: { xs: -0.5, md: 0 },
              '&::-webkit-scrollbar': { height: 6 },
            }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={isMobile ? 0 : 1}
              sx={{
              '& .MuiPaginationItem-root': {
                fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
                minWidth: { xs: 32, sm: 36, md: 40 },
                width: { xs: 32, sm: 36, md: 40 },
                height: { xs: 32, sm: 36, md: 40 },
                borderRadius: 1,
                margin: { xs: '0 2px', sm: '0 3px', md: '0 4px' },
                '&.Mui-selected': {
                  fontWeight: 600,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  boxShadow: (theme) =>
                    theme.palette.mode === 'light'
                      ? '0 2px 4px rgba(0, 0, 0, 0.1)'
                      : '0 2px 4px rgba(0, 0, 0, 0.3)',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                    boxShadow: (theme) =>
                      theme.palette.mode === 'light'
                        ? '0 4px 8px rgba(0, 0, 0, 0.15)'
                        : '0 4px 8px rgba(0, 0, 0, 0.4)',
                  },
                },
                '&:hover': {
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? 'rgba(0, 0, 0, 0.04)'
                      : 'rgba(255, 255, 255, 0.04)',
                },
                transition: (theme) =>
                  theme.transitions.create(['background-color', 'color'], {
                    duration: theme.transitions.duration.short,
                  }),
              },
              '& .MuiPaginationItem-icon': {
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              },
            }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default ParticipantTableSection
